import {logger, Time, Util} from 'yougile-util'
import {DataObjectId, RangePoint, Tag, youGileUserId} from 'yougile-model'
import {Gantt} from '@extensions/gantt'
import {DeadlineCtl} from '@extensions/sticker/controllers/deadline-ctl'
import {Sync} from '../../client/sync'
import {globals} from '../../extensions/globals-base'
import {BoardFilters} from '../../extensions/sticker/board-filters'
import {StickerState} from '../../extensions/sticker/controllers/a-custom-sticker-ctl'
import {Sticker, stickerCtl} from '../../extensions/sticker/sticker'
import {ContextObject} from '../context-object'
import {Item} from './item'
import {Items} from './items'
import {StickerObj} from './sticker-obj'
import {UserObj} from './user-obj'

export type DeadLine = {
  blockedPoints: RangePoint[]
  withTime: boolean
  timestamp: number
  startDate?: number
  deadline?: number
}

/**
 * @description
 * Allows to manipulate stickers.
 * @descriptionRu
 * Позволяет управлять стикерами.
 */
export class Stickers extends ContextObject {
  public static override registered: Map<DataObjectId, Map<string, Stickers>> =
    new Map()

  /**
   * @code
   * Stickers.getAll();
   * @example
   * var allSt = Stickers.getAll();
   * @description
   * Get all stickers that are available in company including system stickers:
   * Timer, Deadline, Stopwatch, User.
   * System stickers can’t be changed, but other stickers can be modified
   * or deleted.
   * @descriptionRu
   * Получает все стикеры, доступные в компании, включая системные стикеры:
   * Таймер, Дедлайн, Секундомер и Юзер.
   * Системные стикеры не могут быть изменениы, но остальные стикеры могут
   * быть модифицированы или удалены.
   */
  public getAll(): StickerObj[] {
    const box = this.context.box
    return Sticker.list(box).map(t => new StickerObj(t.id, box))
  }

  /**
   * @code
   * Stickers.get('');
   * @example
   * var p = Stickers.get('Priority');
   * // now p is a sticker variable,
   * // so we can add a new value to the sticker
   * p.setData('High', {color: '#f00'});
   * @description
   * Gets a sticker by its name. There not only user-defined stickers
   * but also system stickers: 'User', 'Deadline', 'Timer', 'Stopwatch'.
   * @descriptionRu
   * Возвращает стикер по его названию. Кроме пользовательских
   * стикеров есть ещё встроенные системные стикеры, их имена:
   * 'User', 'Deadline', 'Timer', 'Stopwatch' — они также могут
   * быть получены вызовом Stickers.get.
   */
  public get(name: string): StickerObj | undefined {
    const box = this.context.box
    const tag = Sticker.list(box).find(
      t => t.title.toLowerCase() === name.toLowerCase()
    )
    if (!tag) {
      logger.warn(`Sticker “${name}” is not found`)
      return undefined
    }
    return new StickerObj(tag.id, box)
  }

  /**
   * @code
   * Stickers.create('...');
   * @description
   * Creates a new text sticker.
   * @descriptionRu
   * Создание нового текстового стикера.
   * @example
   * var sticker = Stickers.create('Some sticker');
   * var board = Items.get('...');
   * Stickers.addToBoard(board, sticker);
   * sticker.addState('one');
   * sticker.addState('two');
   * sticker.setData('one', {color: '#00f'});
   */
  public create(name: string): StickerObj {
    const {box} = this.context
    const tag = Sticker.create(box, name, 'string', '')
    return new StickerObj(tag.id, box)
  }

  /**
   * @code
   * Stickers.pin(, , );
   * @example
   * // getting task
   * var t = Items.get('ae2ba67f-b6b8-4498-b209-b3db42829b83');
   * // getting sticker
   * var s = Stickers.get('Priority');
   * // adding sticker to the task with value “Major”
   * Stickers.pin(t, s, 'Major');
   * @description
   * Adds the sticker with value to the specified task.
   * If you want to add sticker, but don’t want to specify
   * a value, then you can call the Sticker.pin without 3-rd argument.
   *
   * You can also change the sticker pinned value of the task if the sticker
   * is already pinned to the task.
   * @descriptionRu
   * Добавляет стрикер со значением к конкретной задаче.
   * Если вы хотите добавить стикер, но не хотите указывать значение,
   * мы можете вызвать Sticker.pin без третьего аргумента.
   */
  public pin(task: Item, sticker: StickerObj, value: string): void {
    const tag = Stickers.toTag(sticker)
    const hub = Items.toHub(task)
    if (!value) {
      Sticker.unpin(tag, hub)
      Sticker.pin(tag, 'api', hub, '')
    } else {
      const type = Sticker.getType(tag)

      if (type === 'string' || type === 'sprint') {
        const state = Object.keys(tag.states.index)
          .map(k => tag.states.index[k])
          .find(st => st.name === value) as StickerState

        if (!state) {
          logger.warn(`Sticker state with name ${value} not found.`)
          return
        }
        Sticker.pin(tag, 'api', hub, state.id)
      } else {
        Sticker.pin(tag, 'api', hub, value)
      }
    }
  }

  /**
   * @code
   * Stickers.unpin(, );
   * @example
   * // getting task
   * var t = Items.get('ae2ba67f-b6b8-4498-b209-b3db42829b83');
   * // getting sticker
   * var s = Stickers.get('Priority');
   * // removing sticker from task
   * Stickers.unpin(t, s);
   * @description
   * Removes the sticker from the specified task.
   * @descriptionRu
   * Убирает стикер из указанной задачи.
   */
  public unpin(task: Item, sticker: StickerObj): void {
    Sticker.unpin(Stickers.toTag(sticker), Items.toHub(task))
  }

  /**
   * @code
   * Stickers.isPinned(, );
   * @example
   * // getting task
   * var t = Items.get('ae2ba67f-b6b8-4498-b209-b3db42829b83');
   * // getting sticker
   * var s = Stickers.get('Priority');
   * if (Stickers.isPinned(t, s)) {
   *   Notifier.success('Priority is pinned to the task!');
   * }
   * @description
   * Returns true if sticker is pinned to the task.
   * @descriptionRu
   * Возвращает true если стикер прикреплен к задаче
   */
  public isPinned(task: Item, sticker: StickerObj): boolean {
    return Sticker.isPinned(Stickers.toTag(sticker), Items.toHub(task))
  }

  /**
   * @code
   * Stickers.getValue(, );
   * @example
   * // getting task
   * var t = Items.get('ae2ba67f-b6b8-4498-b209-b3db42829b83');
   * // getting sticker
   * var s = Stickers.get('Priority');
   * if (Stickers.getValue(t, s) === 'Major') {
   *   Notifier.warn('Task is of Major priority!');
   * }
   * @description
   * Gets the value of the sticker pinned to task.
   * @descriptionRu
   * Получает значение стикера, прикрепленного к задаче.
   */
  public getValue(task: Item, sticker: StickerObj): string | undefined {
    const {box} = this.context
    if (!task || box.companyId !== task.box.companyId) {
      return undefined
    }
    const hub = Items.toHub(task)
    const tag = box.get(sticker.id) as Tag
    const val = hub.stickers[tag.id]
    if (!val) {
      return undefined
    }
    const type = Sticker.getType(tag)
    if (type === 'string') {
      return stickerCtl.String.getState(tag, val).name
    } else if (type === 'sprint') {
      return stickerCtl.Sprint.getState(tag, val).name
    } else {
      return Util.clone(val as string)
    }
  }

  /**
   * @code
   * Stickers.getDeadline(Items.get('...'));
   * @description
   * Gets a deadline associated with the task, in format of object
   * {timestamp: number, withTime: boolean}, where timestamp is
   * a server UNIX-timestamp (see App.time()) and withTime tells
   * if specific time of deadline is set or just day is specified.
   * Returns undefined if deadline is not set.
   * @descriptionRu
   * Возвращает дедлайн на задаче в формате
   * {timestamp: number, withTime: boolean},
   * где timestamp – UNIX-timestamp сервера (см. App.time()), а withTime
   * показывает, что установлен не только день дедлайна, но и время.
   * Если дедлайн на задаче не установлен, возвращает undefined.
   * @example
   * var task = Items.get('...');
   * const dl = Stickers.getDeadline(task);
   * if (dl) {
   *   console.log('Deadline is set to ' + App.formatDateTime(dl.timestamp));
   * }
   */
  public getDeadline(task: Item): DeadLine | undefined {
    const {box} = this.context
    if (!task || box.companyId !== task.box.companyId) {
      return undefined
    }
    const sticker = stickerCtl.Deadline.getSticker(box)

    const hub = Items.toHub(task)
    const tag = box.get(sticker.id) as Tag
    const val = hub.stickers[tag.id]

    const blockedPoints = DeadlineCtl.getBlockedPoints(hub)

    return val?.current
      ? {
          blockedPoints,
          withTime: val.current.withTime,
          deadline: val.current.deadline,
          timestamp: val.current.timestamp,
          startDate: val.current.startDate
        }
      : undefined
  }

  /**
   * @code
   * Stickers.getDeadlineHistory(Items.get('...'));
   * @description
   * Gets a deadline history associated with the task, in format of object
   * array of objects:
   * {timestamp: number, withTime: boolean, user: UserObj},
   * where timestamp is
   * a server UNIX-timestamp (see App.time()) and withTime tells
   * if specific time of deadline is set or just day is specified and
   * user is the user who set the deadline.
   * Returns undefined if deadline is not set.
   * @descriptionRu
   * Возвращает историю дедлайна на задаче в формате массива из объектов вида:
   * {timestamp: number, withTime: boolean, user: UserObj},
   * где timestamp – UNIX-timestamp сервера (см. App.time()), а withTime
   * показывает, что установлен не только день дедлайна, но и время,
   * user – пользователь установивший дедлайн.
   * Если дедлайн на задаче не установлен, возвращает undefined.
   * @example
   * var task = Items.get('...');
   * const dlHistory = Stickers.getDeadlineHistory(task);
   * if (dlHistory) {
   *   console.log('Deadline history');
   *   dlHistory.forEach(dl =>
   *     console.log(
   *       'User ' + dl.user.name + ' set deadline to ' +
   *       App.formatDateTime(dl.timestamp)
   *     )
   *   );
   * }
   */
  public getDeadlineHistory(
    task: Item
  ): Array<{withTime: boolean; timestamp: number; user: UserObj}> | undefined {
    const dl = stickerCtl.Deadline.getSticker(task.box)
    const hub = Items.toHub(task)
    if (!Sticker.isPinned(dl, hub)) {
      return undefined
    }
    return hub.stickers[dl.id].history?.map((v: any) => ({
      withTime: v.withTime,
      timestamp: v.deadline,
      user: new UserObj(v.by, task.box)
    }))
  }

  /**
   * @code
   * Stickers.getDeadlineLinks(Items.get('...'));
   * @description
   * Get link task ids.
   * @descriptionRu
   * Получает список id задач, которые связаны с помощью дедлайна (кейс Ганта).
   * @example
   * var task = Items.get('...');
   * Stickers.getDeadlineLinks(task);
   */
  public getDeadlineLinks(task: Item): DataObjectId[] {
    const dl = stickerCtl.Deadline.getSticker(task.box)
    const hub = Items.toHub(task)
    if (!Sticker.isPinned(dl, hub)) {
      return []
    }
    return Gantt.getLinksToHubs(hub).map(hub => hub.id)
  }

  /**
   * @code
   * Stickers.setDeadline(Items.get('...'), App.time() + 60 * 60 * 1000, true);
   * @description
   * Sets a deadline. If timestamp is not specified, then deadline is removed.
   * @descriptionRu
   * Устанавливает дедлайн. Если timestamp не указан, то дедлайн снимается
   * с задачи.
   * @example
   * var task = Items.get('...');
   * // set deadline to now + 1 minute
   * Stickers.setDeadline(task, App.time() + 60000, true);
   * // remove the deadline
   * Stickers.setDeadline(task, undefined);
   */
  public setDeadline(
    task: Item,
    timestamp?: number,
    withTime?: boolean,
    startDate?: number,
    byYougileUser?: boolean
  ): void {
    const dl = stickerCtl.Deadline.getSticker(task.box)
    const hub = Items.toHub(task)
    if (!timestamp) {
      if (Sticker.isPinned(dl, hub)) {
        Sticker.unpin(dl, hub)
      }
      return
    }

    const current = {
      deadline: timestamp,
      timestamp: Time.now(),
      startDate: startDate,
      withTime,
      by: byYougileUser ? youGileUserId : globals.currentUserId
    }
    const history = hub.stickers?.[dl.id]?.history || []
    history.push(current)

    Sticker.pin(dl, 'api', hub, {
      current,
      history
    })
  }

  /**
   * @code
   * Stickers.getFilterValue(, );
   * @description
   * Returns the current value by which board is filtered with the
   * specified sticker.
   * @descriptionRu
   * Возвращает текущее значение, по которому фильтруется доска с
   * указанным стикером.
   */
  public getFilterValue(board: Item, sticker: StickerObj): string[] {
    if (this.context.box.companyId !== board.box.companyId || !sticker) {
      return []
    }
    const box = board.box
    const hub = Items.toHub(board)
    const tag = box.get(sticker.id) as Tag
    return BoardFilters.getForSticker(hub, tag).map(id =>
      tag.states.index[id] ? tag.states.index[id].name : id
    )
  }

  /**
   * @code
   * Stickers.getAllFilters();
   * @description
   * Gets all stickers that are currently filtering the specified board.
   * @descriptionRu
   * Возвращает все стикеры, которые в данный момент фильтруют доску.
   */
  public getAllFilters(board: Item): StickerObj[] {
    if (!board || this.context.box.companyId !== board.box.companyId) {
      return []
    }
    const box = board.box
    return Object.keys(BoardFilters.current[board.id] || {}).map(
      id => new StickerObj(id as DataObjectId, box)
    )
  }

  /**
   * @code
   * Stickers.setFilter(, , );
   * @example
   * // getting board
   * var b = Items.get('…');
   * var s = Stickers.get('Priority');
   * Stickers.setFilter(b, s, 'Major');
   * @description
   * Filter a boards with the specified value of the sticker (only show
   * such tasks that have the specified value of the sticker).
   * @descriptionRu
   * Фильтрует доску с указанным значением стикера (покажет только
   * такие задачи, которые имеют указанное значение стикера).
   */
  public setFilter(board: Item, sticker: StickerObj, value: string): void {
    if (!board || this.context.box.companyId !== board.box.companyId) {
      return
    }
    const tag = Stickers.toTag(sticker)
    const hub = Items.toHub(board)
    const state = Object.keys(tag.states.index)
      .map(k => tag.states.index[k])
      .find(st => st.name === value)
    if (!state) {
      return
    }
    BoardFilters.toggleFilter(hub, tag, state.id)
  }

  /**
   * @code
   * Stickers.removeFilter(, );
   * @description
   * Remove filter by the specified sticker from the board.
   * @descriptionRu
   * Убирает фильтрацию доски по указанному стикеру.
   */
  public removeFilter(board: Item, sticker: StickerObj): void {
    if (!board || this.context.box.companyId !== board.box.companyId) {
      return
    }
    if (BoardFilters.current[board.id]) {
      delete BoardFilters.current[board.id][sticker.id]
      BoardFilters.save()
    }
  }

  /**
   * @code
   * Stickers.getAllInBoard();
   * @description
   * Gets all stickers available in sticker panel of the board.
   * @descriptionRu
   * Получает все стикеры, доступные в панели стикеров доски.
   */
  public getAllInBoard(board: Item): StickerObj[] {
    if (!board || this.context.box.companyId !== board.box.companyId) {
      return []
    }
    return Sticker.list(Items.toHub(board)).map(Stickers.fromTag)
  }

  /**
   * @code
   * Stickers.isInBoard(, );
   * @description
   * Returns true if the specified sticker is added to the board.
   * @descriptionRu
   * Возвращает true, если указанный стикер добавлен в доску.
   */
  public isInBoard(board: Item, sticker: StickerObj): boolean {
    const tag = Stickers.toTag(sticker)
    const hub = Items.toHub(board)
    return Sticker.isInBoard(hub, tag)
  }

  /**
   * @code
   * Stickers.addToBoard(, );
   * @description
   * Adds the sticker to the sticker panel of the specified board.
   * @descriptionRu
   * Добавляет стикер в панель стикеров указанной доски.
   */
  public addToBoard(board: Item, sticker: StickerObj): void {
    const tag = Stickers.toTag(sticker)
    const hub = Items.toHub(board)
    Sticker.addToBoard(hub, tag)
  }

  /**
   * @code
   * Stickers.removeFromBoard(, );
   * @description
   * Remove the sticker from the sticker panel of the board.
   * @descriptionRu
   * Убирает стикер из панели стикеров доски.
   */
  public removeFromBoard(board: Item, sticker: StickerObj): void {
    const tag = Stickers.toTag(sticker)
    const hub = Items.toHub(board)
    Sticker.removeFromBoard(hub, tag)
  }

  /**
   * @code
   * Stickers.onBoardFilterChange = function () {
   *
   * };
   * @description
   * This function is called when user changes board filter stickers.
   * @descriptionRu
   * Функция вызывается когда пользователь меняет
   * фильтр доски (меняет состояние стикера в панели стикеров).
   * @example
   * Stickers.onBoardChange = function () {
   *   var board = Current.board;
   *   var stickers = Stickers.getAllFilters(board);
   *
   *   Notifier.success(
   *     'Фильтрация доски изменена, ' +
   *     'доска ' + board.name + ' фильтрация по: ' +
   *     stickers.map(s => s.name).join()
   *   );
   * };
   */
  public onBoardFilterChange?: () => void

  /**
   * @code
   * Stickers.onPin = function (task, sticker) {
   *   return true;
   * };
   * @description
   * This function is called when user tries to pin sticker to some task.
   * If this function returns false, then the action is prevented.
   * @descriptionRu
   * Эта функция вызывается когда пользователь добавляет стикер на задачу.
   * Если функция возвращает false, действие отменяется.
   * @example
   * Stickers.onPin = function (task, sticker) {
   *   var value = Stickers.getValue(task, sticker);
   *   if (sticker.name === 'Priority' && value === 'Major') {
   *     Notifier.warn('You shouldn’t add this priority!');
   *     return false; // prevent adding sticker
   *   } else {
   *     return true; // default action
   *   }
   * };
   */
  public onPin?: (task: Item, sticker: StickerObj, value: any) => boolean

  /**
   * @code
   * Stickers.onUnpin = function (task, sticker) {
   *   return true;
   * };
   * @description
   * This function is called when user tries to unpin sticker from some task.
   * If this function returns false, then the action is prevented.
   * @descriptionRu
   * Эта функция вызывается когда пользователь убирает стикер с задачи.
   * Если функция возвращает false, действие отменяется.
   * @example
   * Stickers.onUnpin = function (task, sticker) {
   *   if (!Current.user.isAdmin) {
   *     return false; // disallow unpinning if not admin
   *   }
   * }
   */
  public onUnpin?: (task: Item, sticker: StickerObj) => boolean

  public static filterChange(): void {
    Stickers.forEachCurrent((i: Stickers) => {
      if (i.onBoardFilterChange) {
        i.onBoardFilterChange()
      }
      return true
    })
  }

  public static onPin(task: Item, sticker: StickerObj, value: any): boolean {
    if (!task) {
      return true
    }
    if (sticker.name === 'User' && value instanceof Array) {
      return Stickers.forEachCurrent((i: Stickers) =>
        i.onPin ? (i.onPin as any)(task, sticker, ...value) : true
      )
    } else {
      return Stickers.forEachCurrent((i: Stickers) =>
        i.onPin ? i.onPin(task, sticker, value) : true
      )
    }
  }

  public static onUnpin(task: Item, sticker: StickerObj): boolean {
    if (!task) {
      return true
    }
    return Stickers.forEachCurrent((i: Stickers) =>
      i.onUnpin ? i.onUnpin(task, sticker) : true
    )
  }

  /**
   * Utility methods.
   */
  public static toTag(st: StickerObj): Tag {
    return st.box.get(st.id) as Tag
  }

  public static fromTag(t: Tag): StickerObj {
    const box = Sync.get(t)
    if (box.companyId !== globals.getCompanyId()) {
      throw new Error('Company mismatch')
    }
    return new StickerObj(t.id, box)
  }
}
